import React from "react";
import { observer } from "mobx-react";
import { Button, Card, Divider, Grid, Header, Icon, Image, Message, Segment, Table } from "semantic-ui-react";
import PropTypes from "prop-types";
import Immersive3dTourPanorama from "./Immersive3dTourPanorama";
import Immersive3dTourProject from "~/models/Immersive3dTour/Immersive3dTourProject/Immersive3dTourProject";
import ProcessingImageModal from "~/components/ProcessingImageModal";
import ProcessingImageStore from "~/stores/ProcessingImageStore";

@observer
class Immersive3dTourProjectSection extends React.Component {
  getImmersive3dTourOriginalImageComponent(panoramaId) {
    const { getCachedImmersive3dTourOriginalImageCallback } = this.props;

    const immersive3dTourOriginalImage = getCachedImmersive3dTourOriginalImageCallback(panoramaId);
    let image = null;

    if (typeof immersive3dTourOriginalImage !== "undefined") {
      image = <Image src={immersive3dTourOriginalImage.url} size="medium" />;
    }

    return image;
  }

  getImmersive3dTourReplacePanoramaImageComponent(panoramaId) {
    const { getImmersive3dTourReplacePanoramaSignedUrlCallback } = this.props;

    const immersive3dTourReplacePanorama = getImmersive3dTourReplacePanoramaSignedUrlCallback(panoramaId);
    let image = null;

    if (typeof immersive3dTourReplacePanorama !== "undefined") {
      image = <Image src={immersive3dTourReplacePanorama.url} size="medium" />;
    }

    return image;
  }

  render() {
    const {
      immersive3dTourProject,
      goToUploadReplacePanoramaSectionCallback,
      goToUploadMultipleReplacePanoramaSectionCallback,
      orderedReplacePanoramaData,
      removeReplacePanoramaCallback,
      getCachedImmersive3dTourOriginalImageCallback,
      getImmersive3dTourOriginalImageSignedUrlCallback,
      getImmersive3dTourCubeUrlCallback,
      getImmersive3dTourPanoramaZipDownloadUrlCallback,
      uppyFileErrors,
      openProcessingImageModalCallback,
      processingImageStore,
    } = this.props;
    const { panoramasByFloors } = immersive3dTourProject;

    return (
      <>
        <Grid.Row>
          <Grid.Column>
            {uppyFileErrors.map((errorMessage, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Message negative key={index}>
                <p>{errorMessage}</p>
              </Message>
            ))}
            {orderedReplacePanoramaData.length > 0 ? (
              <>
                <Segment clearing>
                  <Button
                    icon
                    primary
                    floated="right"
                    onClick={() => goToUploadMultipleReplacePanoramaSectionCallback()}
                  >
                    <Icon name="cloud upload" /> Upload multiple panoramas
                  </Button>
                  <Button icon positive floated="right" href={getImmersive3dTourPanoramaZipDownloadUrlCallback()}>
                    <Icon name="cloud download" /> Download zip with all panoramas
                  </Button>
                </Segment>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>ID</Table.HeaderCell>
                      <Table.HeaderCell>Old Panorama</Table.HeaderCell>
                      <Table.HeaderCell>New Panorama</Table.HeaderCell>
                      <Table.HeaderCell>Actions</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {orderedReplacePanoramaData.map(replacePanorama => (
                      <Table.Row key={replacePanorama.id}>
                        <Table.Cell>{`#${replacePanorama.id}`}</Table.Cell>
                        <Table.Cell>{this.getImmersive3dTourOriginalImageComponent(replacePanorama.id)}</Table.Cell>
                        <Table.Cell>
                          {this.getImmersive3dTourReplacePanoramaImageComponent(replacePanorama.id)}
                        </Table.Cell>
                        <Table.Cell>
                          <Button
                            basic
                            onClick={event => {
                              event.stopPropagation();
                              removeReplacePanoramaCallback(replacePanorama.id);
                            }}
                          >
                            Cancel
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </>
            ) : (
              <Segment placeholder>
                <Grid columns={2} stackable textAlign="center">
                  <Divider vertical>Or</Divider>

                  <Grid.Row verticalAlign="top">
                    <Grid.Column>
                      <Header>One by one</Header>
                      <p>
                        You can edit one by one the panoramas
                        <br /> by downloading and re-uploading them below.
                      </p>
                      <p>Note: you can process multiple panoramas in one batch.</p>
                    </Grid.Column>

                    <Grid.Column>
                      <Header>Multiple</Header>
                      <p>
                        Download the zip containing all your panoramas and edit the ones that you want. Re-upload the
                        ones you have modified.
                        <br />
                        Make sure not to change the file names.
                      </p>
                      <Button
                        icon
                        positive
                        href={getImmersive3dTourPanoramaZipDownloadUrlCallback()}
                        style={{ marginBottom: "0.5em" }}
                      >
                        <Icon name="cloud download" /> Download zip with all panoramas
                      </Button>
                      <Button icon primary onClick={() => goToUploadMultipleReplacePanoramaSectionCallback()}>
                        <Icon name="cloud upload" /> Upload multiple panoramas
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Segment>
            )}
            {panoramasByFloors.map(floor => (
              <div className="immersive3dTourFloor" key={floor.id}>
                <div className="immersive3dTourPanoramaHeader">
                  <div className="editable-label">
                    <div>
                      <span>{floor.name}</span>
                    </div>
                  </div>
                </div>
                <Card.Group itemsPerRow={5} className="immersive3dTourPanoramas">
                  {floor.panoramas.map(panorama => (
                    <Immersive3dTourPanorama
                      key={panorama.id}
                      panorama={panorama}
                      goToUploadReplacePanoramaSectionCallback={goToUploadReplacePanoramaSectionCallback}
                      getCachedImmersive3dTourOriginalImageCallback={getCachedImmersive3dTourOriginalImageCallback}
                      getImmersive3dTourOriginalImageSignedUrlCallback={
                        getImmersive3dTourOriginalImageSignedUrlCallback
                      }
                      getImmersive3dTourCubeUrlCallback={getImmersive3dTourCubeUrlCallback}
                      openProcessingImageModalCallback={openProcessingImageModalCallback}
                    />
                  ))}
                </Card.Group>
              </div>
            ))}
          </Grid.Column>
        </Grid.Row>
        <ProcessingImageModal processingImageStore={processingImageStore} />
      </>
    );
  }
}

Immersive3dTourProjectSection.propTypes = {
  immersive3dTourProject: PropTypes.instanceOf(Immersive3dTourProject),
  orderedReplacePanoramaData: PropTypes.array.isRequired,
  uppyFileErrors: PropTypes.array.isRequired,
  goToUploadReplacePanoramaSectionCallback: PropTypes.func.isRequired,
  goToUploadMultipleReplacePanoramaSectionCallback: PropTypes.func.isRequired,
  removeReplacePanoramaCallback: PropTypes.func.isRequired,
  getCachedImmersive3dTourOriginalImageCallback: PropTypes.func.isRequired,
  getImmersive3dTourOriginalImageSignedUrlCallback: PropTypes.func.isRequired,
  getImmersive3dTourReplacePanoramaSignedUrlCallback: PropTypes.func.isRequired,
  getImmersive3dTourCubeUrlCallback: PropTypes.func.isRequired,
  getImmersive3dTourPanoramaZipDownloadUrlCallback: PropTypes.func.isRequired,
  openProcessingImageModalCallback: PropTypes.func.isRequired,
  processingImageStore: PropTypes.instanceOf(ProcessingImageStore),
};

export default Immersive3dTourProjectSection;
